import { MCAP_MEDIA_TYPE } from '@/features/mcap/config/constants';
import { parseRetryAfterMs } from '@/shared/lib/retryAfter';

const COMMITTED_RANGE_RE = /^bytes=0-(\d+)$/;

class GcsResumableError extends Error {
  readonly status: number;
  readonly retryAfterMs?: number;

  constructor(status: number, message: string, retryAfterMs?: number) {
    super(message);
    this.name = 'GcsResumableError';
    this.status = status;
    this.retryAfterMs = retryAfterMs;
  }
}

/** Convert GCS `Range: bytes=0-N` into the next byte offset. */
function committedBytesFromRange(value: string | null): bigint | null {
  if (!value) return null;
  const match = COMMITTED_RANGE_RE.exec(value.trim());
  return match ? BigInt(match[1]) + 1n : null;
}

function committedBytesFromResponse(response: Response): bigint {
  const committed = committedBytesFromRange(response.headers.get('Range'));
  if (committed === null) {
    throw new GcsResumableError(response.status, 'upload_offset_unavailable');
  }
  return committed;
}

export async function uploadResumableChunk({
  uploadUri,
  file,
  start,
  endExclusive,
  signal,
  headers,
}: {
  uploadUri: string;
  file: File;
  start: bigint;
  endExclusive: bigint;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}): Promise<bigint> {
  const total = BigInt(file.size);
  if (start < 0n || endExclusive <= start || endExclusive > total) {
    throw new RangeError('invalid resumable chunk bounds');
  }
  const body = file.slice(Number(start), Number(endExclusive));
  const response = await fetch(uploadUri, {
    method: 'PUT',
    headers: {
      'Content-Type': MCAP_MEDIA_TYPE,
      'Content-Range': `bytes ${start}-${endExclusive - 1n}/${total}`,
      ...headers,
    },
    body,
    signal,
    cache: 'no-store',
  });
  if (response.status === 308) {
    return committedBytesFromResponse(response);
  }
  if (response.ok) return total;
  throw new GcsResumableError(
    response.status,
    'upload_chunk_failed',
    parseRetryAfterMs(response.headers.get('Retry-After')),
  );
}
